import { useState, useEffect } from 'react';
import {
  User,
  Mail,
  Calendar,
  MessageSquare,
  StickyNote,
  Plus,
  Trash2,
  Phone,
  Edit2,
  X,
  Check,
  AlertTriangle
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Conversation } from '../../services/omnichannelService';
import { format } from 'date-fns';
import { httpClient } from '../../services/httpClient';
import CustomerJourneyView from './CustomerJourneyView';
import ConversationTags from './ConversationTags';
import SLATracker from './SLATracker';
import ConfirmationModal from '../tickets/ConfirmationModal';

interface CustomerSidebarProps {
  conversation: Conversation;
  onUpdate?: () => void;
}

interface ConversationNote {
  id: string;
  content: string;
  author_id: number;
  author_name?: string;
  created_at: string;
  updated_at: string;
}

interface Agent {
  id: number;
  name: string;
  email: string;
  active_assignments: number;
  max_concurrent_conversations: number;
  is_available: boolean;
}

interface AgentGroup {
  group_id: number;
  group_name: string;
  agents: Agent[];
}

export default function CustomerSidebar({ conversation, onUpdate }: CustomerSidebarProps) {
  const { theme } = useTheme();
  const { user } = useAuthContext();
  const { showToast } = useToast();
  const [notes, setNotes] = useState<ConversationNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [agentGroups, setAgentGroups] = useState<AgentGroup[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [isEditingCustomer, setIsEditingCustomer] = useState(false);
  const [editedPhone, setEditedPhone] = useState(conversation.customer_phone || '');
  const [editedEmail, setEditedEmail] = useState(conversation.customer_email || '');
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [showDeleteNoteConfirm, setShowDeleteNoteConfirm] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<string | null>(null);

  useEffect(() => {
    loadNotes();
    loadAgents();
  }, [conversation.id]);

  const loadAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await httpClient.get<{ success: boolean; data: AgentGroup[] }>(
        `/api/channels/channels/${conversation.channel_id}/available-agents`
      );
      console.log('📦 Available agents for sidebar:', response.data);
      setAgentGroups(response.data || []);
    } catch (error) {
      console.error('Failed to load agents:', error);
      setAgentGroups([]);
    } finally {
      setLoadingAgents(false);
    }
  };

  const handleAssignAgent = async (agentId: number) => {
    if (assigning) return;

    try {
      setAssigning(true);
      await httpClient.post(`/api/channels/conversations/${conversation.id}/assign`, {
        agent_id: agentId
      });
      onUpdate?.();
    } catch (error) {
      console.error('Failed to assign agent:', error);
    } finally {
      setAssigning(false);
    }
  };

  const loadNotes = async () => {
    try {
      setLoadingNotes(true);
      const response = await httpClient.get<{ success: boolean; data: ConversationNote[] }>(
        `/api/channels/conversations/${conversation.id}/notes`
      );
      setNotes(response.data || []);
    } catch (error) {
      console.error('Failed to load notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || savingNote) return;

    try {
      setSavingNote(true);
      await httpClient.post(`/api/channels/conversations/${conversation.id}/notes`, {
        content: newNote.trim()
      });
      setNewNote('');
      await loadNotes();
    } catch (error) {
      console.error('Failed to add note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    setNoteToDelete(noteId);
    setShowDeleteNoteConfirm(true);
  };

  const confirmDeleteNote = async () => {
    if (!noteToDelete) return;

    try {
      await httpClient.delete(
        `/api/channels/conversations/${conversation.id}/notes/${noteToDelete}`
      );
      await loadNotes();
      showToast('Note deleted successfully', 'success');
    } catch (error) {
      console.error('Failed to delete note:', error);
      showToast('Failed to delete note', 'error');
    } finally {
      setShowDeleteNoteConfirm(false);
      setNoteToDelete(null);
    }
  };

  const handleSaveCustomer = async () => {
    try {
      setSavingCustomer(true);
      await httpClient.put(`/api/customers/${conversation.customer_id}`, {
        phone: editedPhone,
        email: editedEmail || null
      });

      // Update local conversation state immediately for real-time UI update
      conversation.customer_phone = editedPhone;
      conversation.customer_email = editedEmail || undefined;

      setIsEditingCustomer(false);
      showToast('Customer information updated successfully', 'success');
      onUpdate?.(); // Trigger parent refresh
    } catch (error) {
      console.error('Failed to update customer:', error);
      showToast('Failed to update customer information', 'error');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedPhone(conversation.customer_phone || '');
    setEditedEmail(conversation.customer_email || '');
    setIsEditingCustomer(false);
  };

  return (
    <div
      className={`w-80 border-l flex flex-col overflow-hidden ${
        theme === 'dark' ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'
      }`}
    >
      <div className="flex-1 overflow-y-auto p-6">
        {/* Customer Info */}
        <div className="text-center mb-6">
          {conversation.customer_avatar ? (
            <img
              src={conversation.customer_avatar}
              alt={conversation.customer_name}
              className="h-20 w-20 rounded-full mx-auto mb-3"
            />
          ) : (
            <div className="h-20 w-20 rounded-full bg-blue-500 flex items-center justify-center mx-auto mb-3">
              <User className="h-10 w-10 text-white" />
            </div>
          )}
          <h3
            className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}
          >
            {conversation.customer_name}
          </h3>
        </div>

        {/* Contact Information */}
        <div className="space-y-4 mb-6">
          <div className="flex items-center justify-between">
            <h4
              className={`text-sm font-semibold uppercase ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}
            >
              Contact Information
            </h4>
            {!isEditingCustomer && (
              <button
                onClick={() => setIsEditingCustomer(true)}
                className={`p-1 rounded transition-colors ${
                  theme === 'dark'
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-200 text-gray-600'
                }`}
                title="Edit customer info"
              >
                <Edit2 className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="space-y-3">
            {/* Phone */}
            <div className="flex items-start gap-3">
              <Phone
                className={`h-5 w-5 mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <div className="flex-1">
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Phone
                </p>
                {isEditingCustomer ? (
                  <input
                    type="tel"
                    value={editedPhone}
                    onChange={(e) => setEditedPhone(e.target.value)}
                    className={`w-full mt-1 px-2 py-1 rounded border text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-200'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                    placeholder="Enter phone number"
                  />
                ) : (
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                    {conversation.customer_phone || 'Not set'}
                  </p>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-3">
              <Mail
                className={`h-5 w-5 mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <div className="flex-1">
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Email
                </p>
                {isEditingCustomer ? (
                  <input
                    type="email"
                    value={editedEmail}
                    onChange={(e) => setEditedEmail(e.target.value)}
                    className={`w-full mt-1 px-2 py-1 rounded border text-sm ${
                      theme === 'dark'
                        ? 'bg-gray-700 border-gray-600 text-gray-200'
                        : 'bg-white border-gray-300 text-gray-700'
                    }`}
                    placeholder="Enter email address"
                  />
                ) : (
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                    {conversation.customer_email || 'Not set'}
                  </p>
                )}
              </div>
            </div>

            {/* Edit Actions */}
            {isEditingCustomer && (
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSaveCustomer}
                  disabled={savingCustomer}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    savingCustomer
                      ? theme === 'dark'
                        ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : theme === 'dark'
                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                  }`}
                >
                  <Check className="h-4 w-4" />
                  {savingCustomer ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={savingCustomer}
                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors ${
                    theme === 'dark'
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Conversation Details */}
        <div className="space-y-4 mb-6">
          <h4
            className={`text-sm font-semibold uppercase ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            Conversation Details
          </h4>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <MessageSquare
                className={`h-5 w-5 mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <div className="flex-1">
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Channel
                </p>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {conversation.channel_name || conversation.channel_type}
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Calendar
                className={`h-5 w-5 mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
              />
              <div className="flex-1">
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Created
                </p>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-900'}`}>
                  {format(new Date(conversation.created_at), 'MMM d, yyyy HH:mm')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Statistics */}
        <div className="space-y-4 mb-6">
          <h4
            className={`text-sm font-semibold uppercase ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            Statistics
          </h4>

          <div
            className={`grid grid-cols-2 gap-3 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
            }`}
          >
            <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
              <p className="text-2xl font-bold">{conversation.message_count}</p>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Messages
              </p>
            </div>

            <div className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}>
              <p className="text-2xl font-bold">{conversation.unread_count}</p>
              <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Unread
              </p>
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-4 mb-6">
          <h4
            className={`text-sm font-semibold uppercase ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}
          >
            <StickyNote className="h-4 w-4 inline mr-2" />
            Notes
          </h4>

          {/* Add Note */}
          <div className="space-y-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note..."
              rows={3}
              className={`w-full px-3 py-2 rounded-lg text-sm resize-none ${
                theme === 'dark'
                  ? 'bg-gray-700 text-gray-200 border-gray-600 placeholder-gray-400'
                  : 'bg-white text-gray-900 border-gray-300 placeholder-gray-500'
              } border focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim() || savingNote}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                !newNote.trim() || savingNote
                  ? theme === 'dark'
                    ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : theme === 'dark'
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              <Plus className="h-4 w-4" />
              {savingNote ? 'Adding...' : 'Add Note'}
            </button>
          </div>

          {/* Notes List */}
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {loadingNotes ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500 mx-auto"></div>
              </div>
            ) : notes.length === 0 ? (
              <p
                className={`text-sm text-center py-4 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                No notes yet
              </p>
            ) : (
              notes.map((note) => (
                <div
                  key={note.id}
                  className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-white'}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p
                      className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}
                    >
                      {note.author_name || `User #${note.author_id}`}
                    </p>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className={`text-red-500 hover:text-red-600 transition-colors ${
                        user?.id !== note.author_id ? 'hidden' : ''
                      }`}
                      title="Delete note"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                  <p
                    className={`text-sm mb-2 whitespace-pre-wrap ${
                      theme === 'dark' ? 'text-gray-200' : 'text-gray-900'
                    }`}
                  >
                    {note.content}
                  </p>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                    {format(new Date(note.created_at), 'MMM d, yyyy HH:mm')}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Conversation Tags */}
        <div className="space-y-4 mb-6">
          <ConversationTags
            conversationId={conversation.id}
            currentTags={conversation.tags}
            onTagsUpdated={onUpdate}
          />
        </div>

        {/* Conversation Journey */}
        <div className="space-y-4">
          <CustomerJourneyView conversationId={conversation.id} />
        </div>
      </div>

      {/* Delete Note Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteNoteConfirm}
        onClose={() => setShowDeleteNoteConfirm(false)}
        onConfirm={confirmDeleteNote}
        title="Delete Note"
        message="Are you sure you want to delete this note? This action cannot be undone."
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
      />
    </div>
  );
}
